'use client';

import { use, useEffect, useState } from 'react';
import { getApolloClient } from '@frameio/graphql-client';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useActiveProjectId } from '@frameio/shared/contexts/ActiveEntitiesContext/ActiveProjectIdContext';
import AppUrlParams from '@frameio/shared/routing/params/params';
import {
  GetAccessRequestById,
  type GetAccessRequestByIdQuery,
} from '../gql/queries/generated/GetAccessRequestById';
import { useIsAccessRequestsEnabled } from './useIsAccessRequestsEnabled';

export const useDeeplinkedAccessRequestIdFromParams = () => {
  const deeplinkedAccessRequestIdFromParam = use(AppUrlParams.ar);
  const isAccessRequestsEnabled = useIsAccessRequestsEnabled();

  const projectId = useActiveProjectId();
  const accountId = useActiveAccountId();
  const client = getApolloClient();

  const [data, setData] = useState<GetAccessRequestByIdQuery | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const shouldSkip =
      !deeplinkedAccessRequestIdFromParam ||
      !isAccessRequestsEnabled ||
      !projectId ||
      !accountId;

    if (shouldSkip) {
      setData(null);
      setError(null);
      return;
    }

    const fetchDeeplinkedAccessRequest = async () => {
      try {
        const result = await client.query<GetAccessRequestByIdQuery>({
          query: GetAccessRequestById,
          variables: {
            accountId,
            accessRequestId: deeplinkedAccessRequestIdFromParam,
          },
          errorPolicy: 'all',
          fetchPolicy: 'network-only',
        });

        if (result.errors && result.errors.length > 0) {
          const graphQLError = result.errors[0];
          setError(
            graphQLError instanceof Error
              ? graphQLError
              : new Error(graphQLError.message || 'GraphQL error')
          );
          setData(null);
          return;
        }

        setData(result.data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setData(null);
      }
    };

    fetchDeeplinkedAccessRequest();
  }, [
    deeplinkedAccessRequestIdFromParam,
    isAccessRequestsEnabled,
    projectId,
    accountId,
    client,
  ]);

  if (!isAccessRequestsEnabled) {
    return {
      deeplinkedAccessRequestId: null,
      deeplinkedAccessRequestData: null,
    };
  }

  let validAccessRequestData;
  let validDeeplinkedAccessRequestId;

  if (error) {
    validDeeplinkedAccessRequestId = 'invalid';
  } else if (data?.account?.accessRequest) {
    validAccessRequestData = data?.account?.accessRequest;
    validDeeplinkedAccessRequestId = deeplinkedAccessRequestIdFromParam;
  }

  return {
    deeplinkedAccessRequestId: validDeeplinkedAccessRequestId,
    accessRequestData: validAccessRequestData,
  };
};
